/**
 * ImmediateActionsPanel — Executive Home "Continue Working" strip.
 *
 * Patch 36.0 · Project Obsidian. These are NOT shortcuts — they are
 * context-aware continuation points computed deterministically from
 * canonical workspace state:
 *
 *   • Continue Business Brain   — Brain not yet approved.
 *   • Review Content            — assets awaiting approval.
 *   • Batch Render              — image prompts ready to render.
 *   • Schedule Approved Content — approved+vaulted assets with no
 *                                 scheduled publication yet.
 *   • Review Publishing         — publications requiring attention.
 *   • Open Vault                — the fallback default; always present
 *                                 so the panel is never empty.
 *
 * The client only reads the canonical CampaignRunSummary + Akinex
 * briefing already surfaced elsewhere — it does not compute business
 * state independently.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

import { useAkinexBriefing, flattenBriefingFindings } from "@/entities/akinex";
import { useCampaignRunSummary } from "@/entities/campaign-run";
import { useWorkspaceMilestones } from "@/entities/workspace/milestones";
import { useCurrentWorkspace } from "@/entities/workspace";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

interface ContinueItem {
  key: string;
  title: string;
  detail: string;
  to: string;
}

function buildImmediateActions(input: {
  hasBrain: boolean;
  brainApproved: boolean;
  drafts: number;
  approved: number;
  scheduled: number;
  hasPublishingAttention: boolean;
  hasPromptsReadyToRender: boolean;
}): ContinueItem[] {
  const items: ContinueItem[] = [];

  if (!input.hasBrain || !input.brainApproved) {
    items.push({
      key: "continue-business-brain",
      title: "Continue Business Brain",
      detail: input.hasBrain
        ? "Approve the Business Brain so campaigns can lock onto it."
        : "Complete Business Brain onboarding to unlock the pipeline.",
      to: "/business-brain",
    });
  }

  if (input.drafts > 0) {
    items.push({
      key: "review-content",
      title: "Review Content",
      detail: `${input.drafts} ${input.drafts === 1 ? "asset is" : "assets are"} waiting for your review.`,
      to: "/campaigns/review",
    });
  }

  if (input.hasPromptsReadyToRender) {
    items.push({
      key: "batch-render",
      title: "Batch Render",
      detail: "Approved prompts are ready to render.",
      to: "/campaigns",
    });
  }

  if (input.approved > input.scheduled) {
    const pending = input.approved - input.scheduled;
    items.push({
      key: "schedule-approved",
      title: "Schedule Approved Content",
      detail: `${pending} approved ${pending === 1 ? "asset is" : "assets are"} ready to schedule.`,
      to: "/publishing/calendar",
    });
  }

  if (input.hasPublishingAttention) {
    items.push({
      key: "review-publishing",
      title: "Review Publishing",
      detail: "Publications need your attention.",
      to: "/publishing/calendar",
    });
  }

  // Fallback keeps the panel meaningful even when everything is calm.
  if (items.length === 0) {
    items.push({
      key: "open-vault",
      title: "Open Vault",
      detail: "Nothing pressing right now — explore approved content.",
      to: "/vault",
    });
  }
  return items.slice(0, 4);
}

export function ImmediateActionsPanel(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const milestones = useWorkspaceMilestones(workspaceId);
  const summaryQ = useCampaignRunSummary(workspaceId);
  const briefingQ = useAkinexBriefing(workspaceId);

  if (!workspaceId || summaryQ.isLoading || briefingQ.isLoading) {
    return (
      <Card
        className="p-5 border-white/10 obsidian-card"
        data-testid="obsidian-immediate-actions"
        data-state="loading"
      >
        <PanelTitle title="Immediate Actions" />
        <div className="mt-4 space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-4/5" />
        </div>
      </Card>
    );
  }

  const counters = summaryQ.data?.counters;
  const findings = briefingQ.data
    ? flattenBriefingFindings(briefingQ.data)
    : [];
  const brainMissing = findings.some((f) => f.key === "brain_missing");
  const brainUnapproved = findings.some(
    (f) => f.key === "brain_unapproved" || f.key === "brain_incomplete",
  );
  const hasPublishingAttention = findings.some(
    (f) => f.key === "publishing_failed" || f.key === "publishing_retrying",
  );
  const hasPromptsReadyToRender = findings.some(
    (f) => f.key === "prompts_ready_to_render",
  );

  const items = buildImmediateActions({
    hasBrain: !brainMissing,
    brainApproved: !brainUnapproved,
    drafts: counters?.draft ?? 0,
    approved: counters?.approved ?? 0,
    scheduled: counters?.scheduled ?? 0,
    hasPublishingAttention,
    hasPromptsReadyToRender,
  });

  const _unused = milestones; // milestones fetch still primes cache.
  void _unused;

  return (
    <Card
      className="p-5 border-white/10 obsidian-card"
      data-testid="obsidian-immediate-actions"
      data-state="populated"
      data-count={items.length}
    >
      <PanelTitle title="Immediate Actions" hint="Continue Working" />
      <ul
        className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2"
        data-testid="obsidian-immediate-actions-list"
      >
        {items.map((item) => (
          <li key={item.key}>
            <Link
              to={item.to}
              data-testid={`obsidian-immediate-${item.key}`}
              className="group flex items-start justify-between gap-3 rounded-lg border border-white/5 bg-black/10 px-3 py-2.5 transition hover:border-white/15 hover:bg-white/[0.03]"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--md-ivory-0)]">
                  {item.title}
                </div>
                <div className="text-[11px] text-[var(--md-fog)]">
                  {item.detail}
                </div>
              </div>
              <ArrowRight
                className="h-3.5 w-3.5 text-[var(--md-fog)] mt-1 shrink-0 transition group-hover:text-[var(--md-ivory-0)] group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PanelTitle({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
        <h3 className="text-sm font-semibold text-[var(--md-ivory-0)]">
          {title}
        </h3>
      </div>
      {hint ? (
        <span className="text-[11px] uppercase tracking-widest text-[var(--md-fog)]">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
